import { createFileRoute } from '@tanstack/react-router';

import { Alert, PageHeader } from '@platform/ui';

import {
  Badges,
  Banners,
  Buttons,
  Forms,
  Overlays,
  Selectables,
  Steps,
  Structures,
  Tables,
} from '../components/design/gallery';
import {
  ColorTokens,
  Radii,
  Section,
  Shadows,
  Spacing,
  TypeScale,
} from '../components/design/tokens';

export const Route = createFileRoute('/design')({
  component: DesignReference,
});

const CONTENTS: { id: string; label: string }[] = [
  { id: 'color', label: 'Color' },
  { id: 'type', label: 'Typography' },
  { id: 'radii', label: 'Radii' },
  { id: 'shadows', label: 'Shadows' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'forms', label: 'Form controls' },
  { id: 'selectables', label: 'Selectable surfaces' },
  { id: 'steps', label: 'Step indicator' },
  { id: 'banners', label: 'Banners' },
  { id: 'badges', label: 'Status pills' },
  { id: 'structures', label: 'Page structures' },
  { id: 'tables', label: 'Tables' },
  { id: 'overlays', label: 'Overlays' },
];

/**
 * The design reference page (FR-UI-04, MASTER_PLAN M2.6): every token and every
 * component in every state, rendered inside the real AppShell so the admin look can be
 * signed off before M11 starts.
 *
 * Token values on this page are read from the live CSS at runtime, never hard-coded, so
 * the page cannot drift from `packages/ui/src/theme.css`.
 */
function DesignReference() {
  return (
    <>
      <PageHeader
        eyebrow="FR-UI-04 · MASTER_PLAN M2.6"
        title="Design reference"
        description="The HeyAllergy design system, extracted from the live booking funnel and encoded as a Tailwind 4 theme in packages/ui. Every swatch below reads its value from the running stylesheet."
      />

      <Alert tone="warning" title="Two brand decisions await owner sign-off.">
        <strong>Q-P15:</strong> the primary blue is <code>#18B3EF</code>, the booking funnel&rsquo;s
        value, rather than the logo&rsquo;s <code>#1BB3EF</code> or the marketing button&rsquo;s{' '}
        <code>#04A6E6</code>. <strong>Q-P16:</strong> the left navigation is white with an{' '}
        <code>#E8F7FE</code> active background and <code>#0B7FB0</code> active text, because the
        only logo asset is full-colour on white. Both are single-variable changes in
        <code> packages/ui/src/theme.css</code> if the owner decides otherwise.
      </Alert>

      <nav aria-label="Design reference sections" className="my-6">
        <ul className="flex flex-wrap gap-2">
          {CONTENTS.map((entry) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                className="inline-block rounded-full border border-neutral-200 bg-surface px-3 py-1 text-2xs font-medium tracking-badge text-neutral-600 uppercase hover:border-primary-hover hover:text-primary-accessible"
              >
                {entry.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <Section
        id="color"
        title="Color"
        description="Hex values are read from the running stylesheet, not written here."
      >
        <ColorTokens />
      </Section>

      <Section
        id="type"
        title="Typography — DM Sans"
        description="The funnel's scale is non-responsive: every size is identical at 390 and at 1280. Only layout changes below 480px."
      >
        <TypeScale />
      </Section>

      <Section id="radii" title="Border radii">
        <Radii />
      </Section>

      <Section id="shadows" title="Shadows">
        <Shadows />
      </Section>

      <Section id="spacing" title="Spacing rhythm">
        <Spacing />
      </Section>

      <Section id="buttons" title="Buttons">
        <Buttons />
      </Section>

      <Section
        id="forms"
        title="Form controls"
        description="Label, help text and error are wired to the control by Field; the 1.5px border and the 10px radius are the funnel's, verbatim."
      >
        <Forms />
      </Section>

      <Section
        id="selectables"
        title="Selectable surfaces"
        description="A selected date chip is a solid blue fill; a selected appointment type is a tinted one. That asymmetry is deliberate and is reproduced exactly."
      >
        <Selectables />
      </Section>

      <Section id="steps" title="Step indicator">
        <Steps />
      </Section>

      <Section id="banners" title="Banners and alerts">
        <Banners />
      </Section>

      <Section id="badges" title="Status pills">
        <Badges />
      </Section>

      <Section id="structures" title="Page structures">
        <Structures />
      </Section>

      <Section id="tables" title="Tables">
        <Tables />
      </Section>

      <Section id="overlays" title="Overlays">
        <Overlays />
      </Section>
    </>
  );
}
